import React, { useEffect, useState, useCallback } from "react";
import {
  MdHistory, MdRefresh, MdClose, MdSearch, MdChevronLeft, MdChevronRight,
  MdReceiptLong, MdPayment, MdLocationOn, MdAccessTime, MdCircle,
  MdDeliveryDining, MdShoppingBag, MdPrint, MdCheck,
} from "react-icons/md";
import { useAuth } from "contexts/AuthContext";
import { getOrdersHistory, getOrderDetail } from "services/ordersService";
import { printClientOrderTicket, printOrderTicket } from "utils/printReceipt";

// ── Status registry (matches the live Orders board) ───────────────────────────
const STATUS = {
  1: { label: "Nouveau",        color: "bg-blue-100 text-blue-700"    },
  2: { label: "Confirmé",       color: "bg-yellow-100 text-yellow-700" },
  3: { label: "En préparation", color: "bg-orange-100 text-orange-700" },
  4: { label: "En route",       color: "bg-purple-100 text-purple-700" },
  5: { label: "Livré",          color: "bg-green-100 text-green-700"  },
  6: { label: "Refusé",         color: "bg-red-100 text-red-600"      },
};

const STATUS_FILTERS = [
  { value: "",  label: "Tous les statuts" },
  { value: "5", label: "Livrées" },
  { value: "6", label: "Refusées" },
  { value: "4", label: "En route" },
  { value: "3", label: "En préparation" },
  { value: "2", label: "Confirmées" },
  { value: "1", label: "Nouvelles" },
];

function fmt(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("fr-FR", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function fmtDay(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function money(value) {
  return Number(value || 0).toFixed(3);
}

function orderTotal(order) {
  if (!order) return 0;
  if (order.orderCost_restaurant != null) return order.orderCost_restaurant;
  if (order.orderCost != null) return order.orderCost;
  if (order.total_restaurant != null) {
    return Math.max(0, Number(order.total_restaurant || 0) - Number(order.deliveryCost || order.delivery_cost || 0));
  }
  return order.total_price ?? order.total ?? 0;
}

function customerName(user) {
  if (!user) return "Client";
  if (user.first_name) return `${user.first_name} ${user.last_name || ""}`.trim();
  return user.name || "Client";
}

function itemsCount(order) {
  return (order.products || []).reduce(
    (sum, p) => sum + Number(p.quantity || p.pivot?.quantity || 1),
    0
  );
}

function orderReceptionMode(order) {
  return order?.restaurant?.order_reception_mode
    || order?.restaurant?.orderReceptionMode
    || order?.order_reception_mode;
}

function orderReceptionLabel(mode) {
  if (mode === "application") return "Application / Web";
  if (mode === "telephone") return "Téléphone";
  return "Non renseigné";
}

function orderReceptionClass(mode) {
  if (mode === "application") return "bg-green-100 text-green-700";
  if (mode === "telephone") return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-500";
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function orderReference(order) {
  return order?.ref || order?.reference || "Référence indisponible";
}

// ── Read-only detail modal ─────────────────────────────────────────────────────
function HistoryDetailModal({ orderId, restaurant, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    getOrderDetail(orderId)
      .then((data) => setOrder(data.data || data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (!orderId) return null;

  const st    = order ? (STATUS[order.status_id] || STATUS[1]) : null;
  const total = order ? orderTotal(order) : 0;

  const timeline = order ? [
    { label: "Commande reçue",  ts: order.created_at,   color: "bg-blue-500"   },
    { label: "Confirmée",        ts: order.confirmed_at, color: "bg-yellow-500" },
    { label: "Commande prete",   ts: order.order_ready_on, color: "bg-green-500"  },
    { label: "Livreur en route", ts: order.onroad_at,    color: "bg-purple-500" },
    { label: "Livrée",           ts: order.delivred_at,  color: "bg-green-500"  },
    { label: "Annulée",          ts: order.canceled_at,  color: "bg-red-500"    },
  ].filter((e) => !!e.ts) : [];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-y-auto rounded-2xl bg-white shadow-xl dark:bg-navy-800">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white p-5 dark:border-navy-700 dark:bg-navy-800">
          {loading ? (
            <div className="h-6 w-48 animate-pulse rounded bg-gray-100 dark:bg-navy-700" />
          ) : (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                  #{orderReference(order)}
                </h3>
                {order?.orderType === "takeaway" && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-600">
                    À emporter
                  </span>
                )}
                {st && (
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.color}`}>
                    {st.label}
                  </span>
                )}
              </div>
              {order?.created_at && (
                <p className="mt-0.5 text-xs text-gray-400">Reçue le {fmt(order.created_at)}</p>
              )}
            </div>
          )}
          <div className="flex flex-shrink-0 items-center gap-1">
            {order && (
              <>
                <button
                  onClick={() => printOrderTicket(order, restaurant)}
                  title="Ticket restaurant"
                  aria-label="Imprimer le ticket restaurant"
                  className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700"
                >
                  <MdPrint className="h-5 w-5" />
                </button>
                <button
                  onClick={() => printClientOrderTicket(order, restaurant)}
                  title="Ticket client"
                  aria-label="Imprimer le ticket client"
                  className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700"
                >
                  <MdReceiptLong className="h-5 w-5" />
                </button>
              </>
            )}
            <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-100 dark:hover:bg-navy-700">
              <MdClose className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : !order ? (
          <div className="flex justify-center py-12 text-sm text-gray-400">Commande introuvable</div>
        ) : (
          <div className="flex-1 space-y-5 p-5">
            {/* Client + Payment */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Client</p>
                <p className="text-sm font-medium text-navy-700 dark:text-white">{customerName(order.user || order.users)}</p>
                {(order.user?.phone || order.users?.phone) && (
                  <p className="mt-0.5 text-xs text-gray-500">{order.user?.phone || order.users?.phone}</p>
                )}
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Paiement</p>
                <p className="flex items-center gap-1 text-sm font-medium text-navy-700 dark:text-white">
                  <MdPayment className="h-4 w-4 text-gray-400" />
                  {order.payementMode?.name || (order.payement_mode_id === 1 ? "Espèces" : "En ligne")}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Canal de réception</p>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${orderReceptionClass(orderReceptionMode(order))}`}>
                  {orderReceptionLabel(orderReceptionMode(order))}
                </span>
              </div>
            </div>

            {/* Address */}
            {order.address && (
              <div>
                <p className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-gray-400">
                  <MdLocationOn className="h-3.5 w-3.5" /> Adresse
                </p>
                <p className="text-sm text-navy-700 dark:text-white">
                  {order.address.address || order.address.street || order.address.label || "—"}
                </p>
              </div>
            )}

            {/* Products */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase text-gray-400">Produits</p>
              <div className="space-y-2">
                {(order.products || []).map((p, i) => {
                  const qty       = p.quantity || p.pivot?.quantity || 1;
                  const lineTotal = p.total_price || p.pivot?.total || (Number(p.price || 0) * qty) || 0;
                  const optionLabels = Array.isArray(p.items)
                    ? p.items.map((item) => item?.name || item).join(", ")
                    : "";
                  return (
                    <div key={i} className="flex items-start gap-3 rounded-xl bg-gray-50 px-3 py-3 dark:bg-navy-700">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-600">
                        {qty}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-base font-semibold leading-snug text-navy-700 dark:text-white">
                          {p.name || p.product?.name || "Produit"}
                        </p>
                        {(p.pivot?.specifications || p.specifications) && (
                          <p className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-white px-3 py-2 text-sm font-medium leading-relaxed text-gray-600 dark:bg-navy-800 dark:text-gray-300">
                            {p.pivot?.specifications || p.specifications}
                          </p>
                        )}
                        {optionLabels && (
                          <p className="mt-2 break-words text-sm leading-relaxed text-gray-500 dark:text-gray-300">Options: {optionLabels}</p>
                        )}
                      </div>
                      <span className="flex-shrink-0 text-sm font-semibold text-navy-700 dark:text-white">
                        {money(lineTotal)} TND
                      </span>
                    </div>
                  );
                })}
                {(!order.products || order.products.length === 0) && (
                  <p className="py-4 text-center text-sm text-gray-400">Aucun produit</p>
                )}
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between border-t border-gray-100 pt-3 dark:border-navy-700">
              <span className="font-bold text-navy-700 dark:text-white">Total</span>
              <span className="text-lg font-bold text-brand-500">{money(total)} TND</span>
            </div>

            {/* Prep time */}
            {order.estimated_preparation_time > 0 && (
              <p className="flex items-center gap-1 text-xs text-gray-400">
                <MdAccessTime className="h-4 w-4" />
                Préparation estimée : {order.estimated_preparation_time} min
              </p>
            )}

            {order.order_ready_on && (
              <p className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                <MdCheck className="h-4 w-4" />
                Commande prete depuis {fmt(order.order_ready_on)}
              </p>
            )}

            {/* Delivery info */}
            {[4, 5].includes(order.status_id) && (
              <div className="flex items-center gap-2 rounded-xl bg-purple-50 px-4 py-3 dark:bg-purple-900/20">
                <MdDeliveryDining className="h-5 w-5 flex-shrink-0 text-purple-500" />
                <div>
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    {order.status_id === 4 ? "Livreur en route" : "Commande livrée"}
                  </p>
                  {order.delivery && (
                    <p className="mt-0.5 text-xs text-purple-500">
                      {order.delivery.first_name || order.delivery.name || "Livreur assigné"}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Refusal reason */}
            {order.status_id === 6 && order.raison && (
              <div className="rounded-xl bg-red-50 px-4 py-3 dark:bg-red-900/20">
                <p className="mb-1 text-xs font-semibold uppercase text-red-400">Motif de refus</p>
                <p className="text-sm text-red-600 dark:text-red-400">{order.raison}</p>
              </div>
            )}

            {/* Timeline */}
            {timeline.length > 0 && (
              <div>
                <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-gray-400">
                  <MdAccessTime className="h-3.5 w-3.5" /> Historique
                </p>
                <div className="space-y-2">
                  {timeline.map((e, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-xs">
                      <MdCircle className={`h-2 w-2 flex-shrink-0 rounded-full ${e.color}`} />
                      <span className="flex-1 text-gray-500 dark:text-gray-400">{e.label}</span>
                      <span className="font-medium text-navy-700 dark:text-white">{fmt(e.ts)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stat card ───────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm dark:bg-navy-800">
      <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${accent}`}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-400">{label}</p>
        <p className="truncate text-xl font-bold text-navy-700 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

// ── Main History page ──────────────────────────────────────────────────────────
export default function History() {
  const { restaurantId, restaurant } = useAuth();

  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [meta,    setMeta]    = useState({ current_page: 1, last_page: 1, total: 0 });
  const [detailId, setDetailId] = useState(null);

  // filters
  const [from,   setFrom]   = useState(() => isoDaysAgo(7));
  const [to,     setTo]     = useState(() => isoDaysAgo(0));
  const [status, setStatus] = useState("");
  const [ref,    setRef]    = useState("");
  const [refInput, setRefInput] = useState("");

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const params = { from, to, page, pagination: 20 };
      if (status) params.status = status;
      if (ref) params.ref = ref;
      const data = await getOrdersHistory(restaurantId, params);
      setOrders(Array.isArray(data) ? data : data.data || []);
      setMeta({
        current_page: data.current_page || 1,
        last_page: data.last_page || 1,
        total: data.total ?? (Array.isArray(data) ? data.length : (data.data || []).length),
      });
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, from, to, status, ref, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to first page whenever a filter changes
  useEffect(() => { setPage(1); }, [from, to, status, ref]);

  const handleSearch = (e) => {
    e.preventDefault();
    setRef(refInput.trim());
  };

  // page-level summary
  const delivered = orders.filter((o) => o.status_id === 5);
  const refused   = orders.filter((o) => o.status_id === 6);
  const pageRevenue = delivered.reduce((sum, o) => sum + Number(orderTotal(o)), 0);

  return (
    <div className="mt-3">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-2xl font-bold text-navy-700 dark:text-white">
          <MdHistory className="h-7 w-7 text-brand-500" />
          Historique des commandes
        </h2>
        <button
          onClick={() => load()}
          className="flex items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
        >
          <MdRefresh className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* Summary cards */}
      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<MdReceiptLong className="h-5 w-5 text-brand-600" />}
          accent="bg-brand-100"
          label="Total (filtré)"
          value={meta.total}
        />
        <StatCard
          icon={<MdShoppingBag className="h-5 w-5 text-green-600" />}
          accent="bg-green-100"
          label="Livrées (page)"
          value={delivered.length}
        />
        <StatCard
          icon={<MdClose className="h-5 w-5 text-red-500" />}
          accent="bg-red-100"
          label="Refusées (page)"
          value={refused.length}
        />
        <StatCard
          icon={<MdPayment className="h-5 w-5 text-purple-600" />}
          accent="bg-purple-100"
          label="CA livré (page)"
          value={`${money(pageRevenue)} TND`}
        />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-navy-800">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-400">Du</label>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-400">Au</label>
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-400">Statut</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <form onSubmit={handleSearch} className="flex-1">
          <label className="mb-1 block text-xs font-semibold text-gray-400">Référence</label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Rechercher par référence..."
              value={refInput}
              onChange={(e) => setRefInput(e.target.value)}
              className="w-full min-w-[140px] rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
            <button
              type="submit"
              className="flex items-center gap-1.5 rounded-xl bg-navy-700 px-3 py-2 text-sm font-semibold text-white hover:bg-navy-800 dark:bg-navy-600"
            >
              <MdSearch className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-navy-800">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-20 text-gray-400">
            <MdHistory className="h-10 w-10" />
            <p className="text-sm">Aucune commande sur cette période</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase text-gray-400 dark:border-navy-700">
                  <th className="px-5 py-3 font-semibold">Référence</th>
                  <th className="px-5 py-3 font-semibold">Date</th>
                  <th className="px-5 py-3 font-semibold">Client</th>
                  <th className="px-5 py-3 text-center font-semibold">Articles</th>
                  <th className="px-5 py-3 text-right font-semibold">Total</th>
                  <th className="px-5 py-3 font-semibold">Statut</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const st = STATUS[order.status_id] || STATUS[1];
                  return (
                    <tr
                      key={order.id}
                      onClick={() => setDetailId(order.id)}
                      className="cursor-pointer border-b border-gray-50 transition-colors hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700"
                    >
                      <td className="px-5 py-3 font-bold text-navy-700 dark:text-white">
                        #{orderReference(order)}
                      </td>
                      <td className="px-5 py-3 text-gray-500 dark:text-gray-400">
                        {fmtDay(order.orderDate || order.created_at)}
                      </td>
                      <td className="px-5 py-3 text-navy-700 dark:text-white">
                        {customerName(order.user || order.users)}
                      </td>
                      <td className="px-5 py-3 text-center text-gray-500 dark:text-gray-400">
                        {itemsCount(order)}
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-navy-700 dark:text-white">
                        {money(orderTotal(order))} TND
                      </td>
                      <td className="px-5 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${st.color}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-xs font-semibold text-brand-500">Détails</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && orders.length > 0 && meta.last_page > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 dark:border-navy-700">
            <span className="text-xs text-gray-400">
              Page {meta.current_page} sur {meta.last_page}
            </span>
            <div className="flex gap-2">
              <button
                disabled={meta.current_page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-navy-700 disabled:opacity-40 dark:border-navy-600 dark:text-white"
              >
                <MdChevronLeft className="h-4 w-4" /> Précédent
              </button>
              <button
                disabled={meta.current_page >= meta.last_page}
                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-navy-700 disabled:opacity-40 dark:border-navy-600 dark:text-white"
              >
                Suivant <MdChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {detailId && (
        <HistoryDetailModal orderId={detailId} restaurant={restaurant} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}
